import { Component, ErrorInfo, ReactNode } from 'react';
import { InitializationErrorScreen } from './initialize-error-screen';
import { classifyError, ErrorClassification } from '../../../util/error-classifier.util';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  classification: ErrorClassification | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    classification: null
  };

  public static getDerivedStateFromError(error: Error): State {
    const classification = classifyError(error);
    return { hasError: true, classification };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const classification = classifyError(error);
    console.error('Uncaught application error:', error, errorInfo);

    if (classification === 'CHUNK_ERROR') {
      const hasReloaded = sessionStorage.getItem('chunk_reload_attempted');
      // Perform a single automatic reload attempt for chunk load failures
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload_attempted', 'true');
        window.location.reload();
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      const classification = this.state.classification || 'UNKNOWN_ERROR';
      
      // We don't want to show the full screen error for AUTH_ERROR, as it typically gets redirected.
      // But if an AUTH_ERROR bubbles here, treat it as unknown/generic.
      const safeClassification = classification === 'AUTH_ERROR' ? 'UNKNOWN_ERROR' : classification;
      
      return <InitializationErrorScreen classification={safeClassification} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
